// API client cho tính năng Sơ đồ tư duy (Mindmap).
// Dùng lại apiFetch (tự gắn Bearer token, auto-refresh, xử lý 402 toàn cục) của apiService.

import { apiFetch, refreshCreditBalance } from '../../services/apiService';
import { API_EDU_PREFIX } from '../../config';
import type {
  Mindmap,
  GeneratedMindmap,
  GenerateMindmapParams,
  Flashcard,
  ReviewQueue,
  ReviewResult,
  ReviewRating,
} from './types';

async function readError(res: Response, fallback: string): Promise<never> {
  const data = await res.json().catch(() => ({}));
  throw new Error((data as any)?.message || (data as any)?.error || fallback);
}

/** Sinh sơ đồ tư duy bằng AI — từ chủ đề hoặc từ nội dung có sẵn. */
export async function generateMindmap(params: GenerateMindmapParams): Promise<GeneratedMindmap> {
  const res = await apiFetch(`${API_EDU_PREFIX}/generate/mindmap`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!res.ok) await readError(res, 'Không tạo được sơ đồ tư duy');
  const data = await res.json();
  refreshCreditBalance('mindmap').catch(() => {});
  return data as GeneratedMindmap;
}

/** Danh sách mindmap của người dùng. */
export async function listMindmaps(): Promise<Mindmap[]> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps`, { method: 'GET' });
  if (!res.ok) await readError(res, 'Không tải được danh sách sơ đồ');
  return (await res.json()) as Mindmap[];
}

/** Chi tiết 1 mindmap (kèm cây node + flashcards). */
export async function getMindmap(id: string | number): Promise<Mindmap> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps/${id}`, { method: 'GET' });
  if (!res.ok) await readError(res, 'Không tải được sơ đồ');
  return (await res.json()) as Mindmap;
}

/** Tạo mới mindmap. */
export async function createMindmap(payload: Partial<Mindmap>): Promise<Mindmap> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) await readError(res, 'Không lưu được sơ đồ');
  return (await res.json()) as Mindmap;
}

/** Cập nhật mindmap. */
export async function updateMindmap(id: string | number, payload: Partial<Mindmap>): Promise<Mindmap> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (!res.ok) await readError(res, 'Không cập nhật được sơ đồ');
  return (await res.json()) as Mindmap;
}

/** Xoá mindmap. */
export async function deleteMindmap(id: string | number): Promise<void> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps/${id}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Không xoá được sơ đồ');
}

/** Sinh bộ thẻ ghi nhớ từ các node của mindmap (đã lưu). */
export async function generateFlashcards(
  id: string | number,
): Promise<{ cards: Flashcard[]; count: number; balance?: number }> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps/${id}/flashcards`, { method: 'POST' });
  if (!res.ok) await readError(res, 'Không tạo được thẻ ghi nhớ');
  const data = await res.json();
  refreshCreditBalance('mindmap-flashcards').catch(() => {});
  return data as { cards: Flashcard[]; count: number; balance?: number };
}

/** Lấy outline văn bản để làm "nguyên liệu" bơm vào luồng sinh nội dung khác. */
export async function getMindmapOutline(id: string | number): Promise<string> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps/${id}/outline`, { method: 'GET' });
  if (!res.ok) await readError(res, 'Không lấy được outline');
  const data = await res.json();
  return (data as any)?.outline || '';
}

/** Hàng đợi thẻ đến hạn ôn (spaced repetition) + thống kê + mastery theo node. */
export async function getReviewQueue(id: string | number): Promise<ReviewQueue> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps/${id}/review`, { method: 'GET' });
  if (!res.ok) await readError(res, 'Không tải được phiên ôn tập');
  return (await res.json()) as ReviewQueue;
}

/** Ghi nhận 1 lần đánh giá thẻ → cập nhật lịch ôn. */
export async function submitReview(
  id: string | number,
  cardId: string,
  rating: ReviewRating,
  nodeId?: string,
): Promise<ReviewResult> {
  const res = await apiFetch(`${API_EDU_PREFIX}/mindmaps/${id}/review`, {
    method: 'POST',
    body: JSON.stringify({ cardId, rating, nodeId }),
  });
  if (!res.ok) await readError(res, 'Không ghi nhận được kết quả ôn tập');
  return (await res.json()) as ReviewResult;
}
